<script module lang="ts">
	export type PartsData = {
		date: Date;
		events: ShowingType[];
	};

	export type ListData = {
		month: Date;
		parts: PartsData[];
	};
</script>

<script lang="ts">
	import { Genres } from '$lib/Globals';
	import type { GenreDataType } from '$lib/types/GenreData';
	import { Breakpoints, DateFormats } from '@tools';
	import type { ShowingType } from '$lib/types/Showings';
	import EventLabels from '@comp/EventLabels.svelte';

	const {
		shows,
		selectedGenres = $bindable([])
	}: {
		selectedGenres: GenreDataType[];
		shows: ShowingType[];
	} = $props();

	function toggleGenre(id: string) {
		const index = selectedGenres.findIndex((g) => g.id === id);
		if (index !== -1) {
			selectedGenres.splice(index, 1);
		} else {
			const target = Genres.getById(id);
			if (target) selectedGenres.push(target);
		}
	}

	const sections: ListData[] = $derived.by(() => {
		const sections: ListData[] = [];

		shows.forEach((show) => {
			const date = new Date(show.date);
			date.setHours(0, 0, 0, 0);
			const month = new Date(date);
			month.setDate(1);

			const section = sections.find((s) => s.month.getTime() === month.getTime());

			if (section) {
				const part = section.parts.find((p) => p.date.getTime() === date.getTime());
				if (part) {
					part.events.push(show);
				} else {
					section.parts.push({
						date,
						events: [show]
					});
				}
			} else {
				sections.push({
					month,
					parts: [
						{
							date,
							events: [show]
						}
					]
				});
			}
		});

		return sections;
	});
</script>

{#each sections as section}
	<div class="month">
		<h3 class="text:h3">
			{DateFormats.monthTitle(section.month)}
		</h3>
		{#each section.parts as day}
			<div class="day">
				<div class="dateContainer">
					<div class={`date ${Breakpoints.md ? 'text:h4' : 'text:body bold'}`}>
						{DateFormats.dayOfMonth(day.date)}
					</div>
					{#if Breakpoints.md}
						<div class="weekday">
							{DateFormats.toString(day.date, { weekday: 'long' })}
						</div>
					{/if}
				</div>
				<div class="events">
					{#each day.events as show}
						<a class="event" href={show.path}>
							<div class="time">
								{DateFormats.timeOfDay(show.date)}
							</div>
							<div class="text">
								<h4 class={Breakpoints.md ? `text:h4` : `text:body`}>{show.title}</h4>
								<p class={Breakpoints.md ? `text:body` : `text:body-sm`}>{show.subtitle}</p>
								{#if !Breakpoints.md}
									<div class="genres text:body-sm">
										{#if show.genres}
											{#each show.genres as genre}
												<span
													class="genre"
													role="button"
													tabindex="0"
													onkeydown={(e) => {
														if (e.key === 'Enter' || e.key === ' ') {
															toggleGenre(genre.id);
															e.preventDefault();
														}
													}}
													onclick={(e) => {
														toggleGenre(genre.id);
														e.preventDefault();
													}}>#{genre.slug.replace('born', 'børn')}</span
												>
											{/each}
										{/if}
									</div>
								{/if}
							</div>

							<EventLabels status={show.status} badge={show.badge} responsive={true} />
							{#if Breakpoints.md}
								<div class="genres">
									{#if show.genres}
										{#each show.genres as genre}
											<span
												class="genre"
												role="button"
												tabindex="0"
												onkeydown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														toggleGenre(genre.id);
														e.preventDefault();
													}
												}}
												onclick={(e) => {
													toggleGenre(genre.id);
													e.preventDefault();
												}}>#{genre.slug.replace('born', 'børn')}</span
											>
										{/each}
									{/if}
								</div>
							{/if}
						</a>
					{/each}
				</div>
			</div>
		{/each}
	</div>
{/each}

<style lang="scss">
	.month {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		padding-bottom: $gap8;
	}

	h3 {
		grid-column: 1 / -1;
		position: sticky;
		top: var(--nav-height);
		z-index: 5;
		line-height: var(--nav-height);
		background-color: $colorBackground;
		padding: 0 var(--gutter);
		border-bottom: 1px solid currentColor;
		text-transform: uppercase;
	}

	.day {
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		border-top: 1px solid currentColor;

		&:nth-child(2) {
			border-top: none;
		}

		&:nth-last-child(1) .event:nth-last-child(1) {
			border-bottom: 1px solid currentColor;
		}
	}

	.dateContainer {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: $gap3 var(--gutter);
		padding-right: $gap2;
		text-transform: capitalize;
		position: sticky;
		top: calc(var(--nav-height) * 2);

		@include md {
			flex: 0 0 cols(12, 2, true);
			padding-right: 0;
			height: var(--nav-height);
		}
	}

	.date {
		/* -moz-font-feature-settings: 'tnum';
		-webkit-font-feature-settings: 'tnum';
		font-feature-settings: 'tnum' on; */
		font-variant-numeric: tabular-nums;

		font-family: $rootFamily;
		font-size: 15px;
		font-weight: 500;
		line-height: 1.4;

		@include md {
			font-family: $titleFamily;
			font-size: 32px;
			font-weight: 400;
			line-height: 1.1em;
		}
	}

	.events {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
		align-items: stretch;
	}

	.event {
		display: flex;
		border-bottom: 1px solid currentColor;
		align-items: start;
		min-height: var(--nav-height);
		padding: $gap3 var(--gutter);
		padding-left: $gap3;
		gap: var(--gap);
		text-decoration: none;
		transition:
			background-color $transition,
			padding-left $transition;

		&:hover {
			background-color: $colorHover;
			padding-left: #{$gap3 + $gap2};
		}

		&:nth-last-child(1) {
			border-bottom: none;
		}

		:global([data-component='EventLabels']) {
			flex: 0 0 auto;
			flex-direction: row;
		}

		@include md {
			padding-left: var(--gap);
			align-items: center;

			&:hover {
				padding-left: calc(var(--gap) + $gap3);
			}
		}
	}

	.time {
		flex: 0 0 0;

		@include md {
			flex-basis: cols(10, 1);
		}
	}

	.text {
		flex: 1 1 0;
		display: flex;
		flex-direction: column;
		justify-content: center;
		height: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		gap: $gap1;

		h4 {
			font-weight: 500;
		}
	}

	.genres {
		flex: 0 0 cols(10, 2);
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		overflow: hidden;
		text-overflow: ellipsis;
		gap: 0 0.25rem;
	}

	.genre {
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}
</style>
